import React, { useState, useEffect } from "react";
import { useSnackBar } from "../../../hooks/useSnackBar";
import "./Companies.css";

// components imports
import CarriersTable from "../../../Components/BoltTable/BoltTable";
import Tooltip from "../../../Components/Tooltip/Tooltip";
import SwitchButton from "../../../Components/SwitchButton/SwitchButton";
import EditCompany from "./Components/EditCompany/EditCompany";
import ManageServices from "./Components/ManageServices/ManageServices";

// apis imports
import {
  fetchShippingCompanies,
  updateShippingCompanyStatus,
  fetchCustomCarriers,
  addCustomCarrierAccount,
  updateCustomCarrierStatus,
} from "../../../services/AdminServices/Companies/companies.services";

// library imports
import PlaceholderImage from "../../../assets/images/placeholder-image.png";
import { MdOutlineEditCalendar } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import { FcPlus } from "react-icons/fc";
import TabsComponent from "../../../Components/Common/TabsComponent";
import BoltButton from "../../../Components/UI/Buttons/BoltButton";
import AddCustomCarrier from "../../Shipper/Customer/MainPages/Carriers/Components/AddCustomCarrier/AddCustomCarrier";
import { useTranslation } from "react-i18next";

const Companies = () => {
  const { t } = useTranslation("admin");
  const [tab, setTab] = useState(0);
  const [editCompanyModal, setEditCompanyModal] = useState();
  const [manageServicesModal, setManageServicesModal] = useState(false);
  const [addAccountModal, setAddAccountModal] = useState();
  const [loader, setLoader] = useState(false);
  const [shippingCompanies, setShippingCompanies] = useState({ data: [] });
  const [customCarriers, setCustomCarriers] = useState({ data: [] });

  const setSnack = useSnackBar();

  const tabPermissions = [
    { label: t("companies.mainCarriers"), value: 0, style: { fontWeight: "bold" } },
    { label: t("companies.customCarriers"), value: 1, style: { fontWeight: "bold" } },
  ];

  const UpdateCompanyStatus = async (check, id) => {
    setLoader("status");

    // Use different API based on current tab
    const res =
      tab === 0
        ? await updateShippingCompanyStatus({ is_enable: `${check}` }, id)
        : await updateCustomCarrierStatus({ is_enable: `${check}` }, id);

    if (res.error) {
      setLoader(false);
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    } else {
      if (tab === 0) {
        // Update Hajex carriers
        setShippingCompanies((prevShippingCompanies) => {
          return {
            ...prevShippingCompanies,
            data: prevShippingCompanies?.data.map((company) => {
              return company.id === id
                ? { ...company, is_enable: `${check}` }
                : company;
            }),
          };
        });
      } else {
        // Update custom carriers
        setCustomCarriers((prevCustomCarriers) => {
          return {
            ...prevCustomCarriers,
            data: prevCustomCarriers?.data.map((carrier) => {
              return carrier.id === id
                ? {
                    ...carrier,
                    is_enable: { ...carrier?.is_enable, admin: check },
                  }
                : carrier;
            }),
          };
        });
      }

      setLoader(false);
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("companies.statusUpdatedSuccessfully"),
          severity: "success",
        };
      });
    }
  };

  const getAllShippingCompanies = async (page, perPage, filter) => {
    const response = await fetchShippingCompanies(page, perPage, filter);
    if (!response.error) {
      setShippingCompanies(response?.data?.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  const getAllCustomCarriers = async (page, perPage, filter) => {
    const response = await fetchCustomCarriers(page, perPage, filter);
    if (!response.error) {
      setCustomCarriers(response?.data?.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  // Load initial data
  useEffect(() => {
    loadAvailableCarriers();
  }, []);

  // Load available carriers for AddCustomCarrier modal
  const loadAvailableCarriers = async () => {
    try {
      const response = await fetchShippingCompanies(1, 1000, ""); // Get all carriers
      if (!response.error && response?.data?.result?.data) {
        // Store carriers in localStorage for AddCustomCarrier component
        localStorage.setItem(
          "Carriers",
          JSON.stringify(response.data.result.data)
        );
      }
    } catch (error) {
      console.error("Error loading carriers:", error);
    }
  };

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 1,
      label: t("companies.status"),
      render: (row) => (
        <>
          <SwitchButton
            checked={[true, "true", "1"].includes(row.is_enable)}
            onChange={(e, value) => UpdateCompanyStatus(value, row.id)}
            disabled={loader === "status" ? true : false}
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 2,
      label: t("companies.name"),
      render: (row) => <>{row?.name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 3,
      label: t("companies.logo"),
      render: (row) => (
        <>
          <img
            src={row.logo_url}
            alt={row.name || t("companies.carrierLogo")}
            onError={(e) => {
              e.target.src = PlaceholderImage;
            }}
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 4,
      label: t("companies.sandboxUrl"),
      render: (row) => (
        <div className="companies_text_blur">{row?.api_url ?? "-"}</div>
      ),
      width: "200px",
    },
    {
      id: 5,
      label: t("companies.productionUrl"),
      render: (row) => (
        <div className="companies_text_blur">{row?.production_url ?? "-"}</div>
      ),
      width: "200px",
    },
    {
      id: 6,
      label: t("companies.username"),
      render: (row) => (
        <div className="companies_text_blur">{row?.user_name ?? "-"}</div>
      ),
      width: "200px",
    },
    {
      id: 7,
      label: t("companies.password"),
      render: (row) => (
        <div className="companies_text_blur">{row?.password ?? "-"}</div>
      ),
      width: "200px",
    },
    {
      id: 8,
      label: t("companies.accountNo"),
      render: (row) => (
        <div className="companies_text_blur">{row?.account_no ?? "-"}</div>
      ),
      width: "200px",
    },
    {
      id: 6,
      label: t("companies.actions"),
      render: (row) => (
        <div className="table_action__wrap">
          <Tooltip title={t("companies.manageServices")}>
            <div
              className="table_action__btn"
              onClick={() => {
                setManageServicesModal(row);
              }}
            >
              <MdOutlineEditCalendar size={10} />
              {t("companies.services")}
            </div>
          </Tooltip>
          <Tooltip title={t("companies.editCompany")}>
            <div
              className="table_action__btn"
              onClick={() => {
                setEditCompanyModal(row);
              }}
            >
              <FaRegEdit size={10} />
              {t("common:actions.edit")}
            </div>
          </Tooltip>
        </div>
      ),
      width: "200px",
      fixed: "right",
    },
  ];

  // Columns for Custom Carriers
  const customCarrierColumns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 1,
      label: t("companies.carrierImage"),
      render: (row) => (
        <>
          <img
            src={row?.shipping_company?.logo_url}
            alt={row?.shipping_company?.name || t("companies.carrierLogo")}
            onError={(e) => {
              e.target.src = PlaceholderImage;
            }}
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 2,
      label: t("companies.carrierName"),
      render: (row) => <>{row?.shipping_company?.name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 3,
      label: t("companies.customAccountName"),
      render: (row) => <>{row?.account_name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 4,
      label: t("companies.status"),
      render: (row) => (
        <>
          <SwitchButton
            checked={[true, "true", "1"].includes(row.is_enable?.admin)}
            onChange={(e, value) => UpdateCompanyStatus(value, row.id)}
            disabled={loader === "status" ? true : false}
          />
        </>
      ),
      width: "200px",
    },
  ];

  return (
    <div className="dispatcher__screen" style={{ maxWidth: "100%" , padding: "0px 5px"}}>

      <TabsComponent
        items={tabPermissions}
        tab={tab}
        handleTabChange={handleTabChange}
      />

      <EditCompany
        open={editCompanyModal}
        setOpen={setEditCompanyModal}
        setShippingCompanies={setShippingCompanies}
        previousData={editCompanyModal}
        setSnack={setSnack}
      />
      <ManageServices
        open={manageServicesModal}
        setOpen={setManageServicesModal}
        carrier={manageServicesModal}
        setShippingCompanies={setShippingCompanies}
        Android12Switch={SwitchButton}
        setSnack={setSnack}
      />

      <AddCustomCarrier
        open={!!addAccountModal}
        data={addAccountModal}
        setOpen={setAddAccountModal}
        setCarriers={setCustomCarriers}
        setSnack={setSnack}
        getAllCarriers={getAllCustomCarriers}
        isAdmin={true}
        addCustomCarrierAccount={addCustomCarrierAccount}
        carriers={JSON.parse(localStorage.getItem("Carriers") || "[]")}
      />

      <CarriersTable
        tableTitle={tab === 0 ? t("companies.mainCarriers") : t("companies.customCarriers")}
        tableId="companiesTableForCarriers"
        message={
          tab === 0
            ? t("companies.noMainCarriers")
            : t("companies.noCustomCarriers")
        }
        searchPlaceHolder={t("companies.searchCarriers")}
        columns={tab === 0 ? columns : customCarrierColumns}
        data={tab === 0 ? shippingCompanies : customCarriers}
        getAllData={tab === 0 ? getAllShippingCompanies : getAllCustomCarriers}
        refresh={tab}
        headerButtons={
          <div className="flex gap-1 flex-wrap">
            {tab === 1 && (
              <BoltButton
                icon={FcPlus}
                label={t("companies.addAccountCaps")}
                onClick={() => setAddAccountModal(true)}
                variant="outline"
                size="sm"
              />
            )}
          </div>
        }
      />
    </div>
  );
};

export default Companies;
